import { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { doc, p } from '@atlaskit/adf-utils/builders';

import { CreateJiraWorkItemModal } from 'src/components/create-jira-work-item-modal';
import { getRepository } from 'src/components/pipelines/redux/selectors/pipelines';

import { messages } from '../messages';

type CreateJiraWorkItemFromTestModalProps = {
  isOpen: boolean;
  testName: string;
  testUuid: string;
  cloudId: string;
  projectId?: string;
  onClose: () => void;
};

const GIC_CONSUMER = 'bitbucketTestManagement';
const FLAG_ID_PREFIX = 'create-jira-work-item-from-test';

/**
 * Test-management-specific wrapper around the reusable
 * `CreateJiraWorkItemModal`. Builds the test-specific summary and ADF
 * description (test name, repository, test id) and delegates the actual modal
 * + GIC iframe + flag handling to the shared component.
 */
export const CreateJiraWorkItemFromTestModal = ({
  isOpen,
  testName,
  testUuid,
  cloudId,
  projectId,
  onClose,
}: CreateJiraWorkItemFromTestModalProps) => {
  const { formatMessage } = useIntl();
  const repository = useSelector(getRepository);

  const summary = formatMessage(messages.createJiraWorkItemSummary, {
    testName,
  });

  const descriptionAdf = useMemo(
    () =>
      doc(
        p(
          formatMessage(messages.createJiraWorkItemDescriptionLine1, {
            repository: repository.full_name || repository.displayName || '',
          })
        ),
        p(
          formatMessage(messages.createJiraWorkItemDescriptionLine2, {
            testName,
          })
        ),
        p(
          formatMessage(messages.createJiraWorkItemDescriptionLine3, {
            testUuid,
          })
        )
      ),
    [
      formatMessage,
      repository.full_name,
      repository.displayName,
      testName,
      testUuid,
    ]
  );

  return (
    <CreateJiraWorkItemModal
      isOpen={isOpen}
      onClose={onClose}
      cloudId={cloudId}
      projectId={projectId}
      summary={summary}
      descriptionAdf={descriptionAdf}
      consumer={GIC_CONSUMER}
      flagIdPrefix={FLAG_ID_PREFIX}
    />
  );
};
